import { IconSearch, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";
import { Button } from "./buttons/button";

export function Searchbar({
	value = "",
	onClear,
	onChange,
	onClick,
	className,
	showButton = false,
	debounceTimeout = 300,
}: {
	value?: string;
	onClear?: () => void;
	onChange?: (value: string) => void;
	onClick?: (value: string) => void;
	className?: string;
	showButton?: boolean;
	debounceTimeout?: number;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [inputValue, setInputValue] = useState(value);

	useEffect(() => {
		if (inputRef.current && value) {
			inputRef.current.focus();
		}
	}, [value]);

	const debouncedOnChange = useRef(
		debounce((nextValue) => {
			onChange?.(nextValue);
		}, debounceTimeout),
	).current;

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue);

		if (newValue === "") {
			debouncedOnChange.cancel();

			onChange?.(newValue);
		} else {
			debouncedOnChange(newValue);
		}
	};

	const handleOnClick = () => {
		onClick?.(inputValue);
	};

	const handleClear = () => {
		setInputValue("");
		onClear?.();
	};

	return (
		<div className={clsx("flex flex-row items-center w-full gap-1", className)}>
			<div className="flex flex-row items-center w-full h-[38px] border border-border rounded-md overflow-hidden">
				<div className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50">
					<IconSearch size={18} stroke={1.5} />
				</div>
				<input
					ref={inputRef}
					placeholder="Looking for something?"
					className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
					value={inputValue}
					autoComplete="off"
					onChange={handleOnChange}
				/>

				{inputValue && (
					<button
						onClick={handleClear}
						type="button"
						className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50 hover:opacity-100"
					>
						<IconX size={18} stroke={1.5} />
					</button>
				)}
			</div>
			{showButton && <Button onClick={() => handleOnClick()}>Search</Button>}
		</div>
	);
}
